import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const noticeDotVariants = cva("shrink-0 rounded-full", {
  variants: {
    size: { sm: "size-1.5", md: "size-2" },
    tone: {
      neutral: "bg-text-muted",
      info: "bg-info",
      success: "bg-success",
      warning: "bg-warning",
      error: "bg-destructive",
    },
  },
  defaultVariants: { tone: "neutral", size: "md" },
});

export type NoticeDotProps = React.ComponentPropsWithoutRef<"span"> &
  VariantProps<typeof noticeDotVariants>;

/** Decorative outcome marker; supply an adjacent text label for its meaning. */
export const NoticeDot = React.forwardRef<HTMLSpanElement, NoticeDotProps>(
  ({ tone, size, className, ...props }, ref) => (
    <span
      {...props}
      ref={ref}
      aria-hidden="true"
      className={cn(noticeDotVariants({ tone, size }), className)}
    />
  ),
);
NoticeDot.displayName = "NoticeDot";

export type InlineNoticeProps = React.ComponentPropsWithoutRef<"span"> &
  VariantProps<typeof noticeDotVariants>;

/** Compact status text with a decorative semantic dot. No live announcement by default. */
export const InlineNotice = React.forwardRef<
  HTMLSpanElement,
  InlineNoticeProps
>(({ tone, size = "md", className, children, ...props }, ref) => (
  <span
    ref={ref}
    data-slot="inline-notice"
    className={cn(
      "inline-flex min-w-0 items-baseline font-medium text-text-primary",
      size === "sm" ? "gap-1.5 body-sm" : "gap-2 body-md",
      className,
    )}
    {...props}
  >
    <NoticeDot tone={tone} size={size} />
    <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
  </span>
));
InlineNotice.displayName = "InlineNotice";
